import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { GeoJSON, FeatureGroup, Tooltip } from 'react-leaflet';
import _ from 'lodash';
import { useEvents, useTelemetry, useDevices, useFields, useOrganizations, useProperties } from 'hooks';

import { Leaflet, ProbeMarker, EditFieldPoints } from 'components/Leaflet';

const FieldMap = ({ editingField, updateFieldGeometry, setMap, polyRef, setZooming }) => {
  const { selectedDevices } = useDevices();
  const { fields, setSelectedField, selectedField, fieldBounds } = useFields();
  const { selectedFieldId } = useFields();
  const { selectedPropertyId } = useProperties();
  const { selectedOrganizationId } = useOrganizations();
  const { events } = useEvents();
  const { telemetryPeriod, telemetryAverages } = useTelemetry();
  const fieldLayers = [];
  const { t } = useTranslation();
  const [map, setMapState] = useState();
  const activeFieldDevices = selectedDevices ? selectedDevices : [];

  const filterFields = () => {
    if (selectedFieldId && selectedField) {
      return [selectedField];
    } else if (selectedPropertyId) {
      return _.filter(fields, { property_id: selectedPropertyId });
    } else if (selectedOrganizationId) {
      return _.filter(fields, { organization_id: selectedOrganizationId });
    }
  };

  const filteredFields = filterFields();

  const polygonMouseOver = (event) => {
    const layer = event.target;
    layer.setStyle({
      fillOpacity: '30%',
    });
  };

  const polygonMouseOut = (event) => {
    const layer = event.target;
    layer.setStyle({
      fillOpacity: '15%',
    });
  };

  const onMapCreate = (map) => {
    setMapState(map);
    setMap(map);
  };

  const renderProbeMarkers = (probes) => {
    const probeMarkers = [];
    const selectProbe = (id, toggle) => {
      if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
        console.log('SelectedProbe', id, toggle);
      }
      //TODO: Not sure why selecting it does nothing.
    };

    _.forEach(probes, (probe) => {
      const selected = selectedField ? probe.field_id === selectedField.id : false;
      const probeEvents = _.find(events, (value, key) => key === probe.id);
      const missingReadings = false;
      if (probe?.location?.coordinates) {
        probeMarkers.push(
          <ProbeMarker
            key={`${probe.id}_probe`}
            missingReadings={missingReadings}
            telemetry={_.get(telemetryAverages, [probe.hw_addr, telemetryPeriod])}
            id={probe.id}
            probe={probe}
            events={probeEvents}
            selected={selected}
            onClick={() => selectProbe(probe.id, false)}
            position={{
              lat: Number(probe.location.coordinates[1]),
              lon: Number(probe.location.coordinates[0]),
            }}
          />
        );
      }
    });
    return probeMarkers;
  };

  const getFieldTooltip = (field) => {
    return (
      <div>
        <div className="tooltip-header">{t('field')}</div>
        <div className="tooltip-field" style={{ marginBottom: 4, marginTop: -10 }}>
          <b>{field.name}</b>
        </div>
        <div className="tooltip-field">
          <b style={{ marginRight: 10, width: 65 }}>{t('area')}:</b>
          {field.area + ' ' + t('hectares')}
        </div>
      </div>
    );
  };

  const getFieldGeoJSON = (field, background) => {
    const p = {
      fillOpacity: '15%',
    };
    if (background) {
      p.color = '#FFFFFF';
      p.stroke = '#FFFFFF';
      p.fill = '#FFFFFF';
    } else {
      p.color = '#3388FF';
      p.stroke = '#3388FF';
      p.fill = '#3388FF';
    }

    return (
      <GeoJSON
        pathOptions={p}
        {...p}
        onMouseover={polygonMouseOver}
        onFocus={polygonMouseOver}
        onMouseout={polygonMouseOut}
        onBlur={polygonMouseOut}
        data={field.geometry}
        key={field.id}
        onClick={() => {
          setSelectedField(field.id);
        }}
      >
        <Tooltip sticky>{getFieldTooltip(field)}</Tooltip>
      </GeoJSON>
    );
  };
  _.forEach(filteredFields, (field) => {
    if (field.geometry) {
      if (_.isEmpty(editingField)) {
        fieldLayers.push(getFieldGeoJSON(field, false));
      } else if (field.id !== editingField?.id) {
        fieldLayers.push(getFieldGeoJSON(field, true));
      }
    }
  });

  const probeMarkers = [];
  const selectProbe = (id, toggle) => {
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
      console.log('SelectedProbe', id, toggle);
    }
  };
  _.forEach(activeFieldDevices, (probe) => {
    const missingReadings = false; // ReadingsHelpers.missingReadings(probe.viewTelemetry, chartReadings);
    const selected = selectedField ? probe.field_id === selectedField.id : false; // _.indexOf(selectedProbesEUIs, probe.id) !== -1;
    const showReadings = false;
    const chartReadings = false;
    if (probe.location && probe.location.geometry && probe.location.geometry.coordinates) {
      probeMarkers.push(
        <ProbeMarker
          key={`${probe.id}_${selected}`}
          missingReadings={missingReadings}
          showReadings={showReadings && chartReadings}
          id={probe.id}
          probe={probe}
          selected={selected}
          onClick={() => selectProbe(probe.id, false)}
          position={{
            lat: Number(probe.location.geometry.coordinates[1]),
            lon: Number(probe.location.geometry.coordinates[0]),
          }}
        />
      );
    }
  });

  return (
    <Leaflet
      key="heightMap"
      features={probeMarkers}
      handleEdit={updateFieldGeometry}
      bounds={fieldBounds}
      onMapCreate={onMapCreate}
      setZooming={setZooming}
    >
      <>
        {!_.isEmpty(editingField) && <EditFieldPoints polyRef={polyRef} field={editingField} map={map} />}
        <FeatureGroup>{renderProbeMarkers(selectedDevices)}</FeatureGroup>
        <FeatureGroup>{fieldLayers}</FeatureGroup>
      </>
    </Leaflet>
  );
};

FieldMap.propTypes = {
  editingField: PropTypes.object,
  updateFieldGeometry: PropTypes.func,
  setMap: PropTypes.func.isRequired,
  polyRef: PropTypes.object,
  setZooming: PropTypes.func.isRequired,
};

FieldMap.defaultProps = {
  editingField: undefined,
  updateFieldGeometry: undefined,
  polyRef: undefined,
};

export default FieldMap;
